<template>
  <div class="omni-dashboard">
    <div
      v-if="isLoadingUrl"
      class="flex items-center justify-center h-[600px] text-neutral-text-strong"
    >
      Loading dashboard...
    </div>
    <div
      v-else-if="errorMessage"
      class="flex items-center justify-center h-[600px] text-error-text-strong"
    >
      {{ errorMessage }}
    </div>
    <iframe
      v-else-if="embedUrl"
      ref="iframeRef"
      :src="embedUrl"
      title="Analytics dashboard"
      class="w-full h-full min-h-[600px]"
      frameborder="0"
      allowfullscreen
    />
  </div>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, ref } from 'vue';

import { crossbeamApi } from '@/api';

type Props = {
  dashboardId: string;
};

const props = defineProps<Props>();

const embedUrl = ref('');
const isLoadingUrl = ref(true);
const errorMessage = ref('');
const iframeRef = ref<HTMLIFrameElement | null>(null);

async function generateEmbedUrl() {
  try {
    isLoadingUrl.value = true;
    errorMessage.value = '';

    const { data, error } = await crossbeamApi.GET('/v0.1/omni/embed-url', {
      params: {
        query: {
          dashboard_id: props.dashboardId,
        },
      },
    });

    if (error) {
      throw error;
    }

    if (!data?.embed_url) {
      throw new Error('No embed URL returned from server');
    }

    embedUrl.value = data.embed_url;
  } catch (error) {
    errorMessage.value = `Failed to generate embed URL: ${error instanceof Error ? error.message : String(error)}`;
  } finally {
    isLoadingUrl.value = false;
  }
}

function handleMessage(event: MessageEvent) {
  // Extract organization name from the embed URL origin
  const embedUrlOrigin = embedUrl.value ? new URL(embedUrl.value).origin : null;

  if (!embedUrlOrigin || event.origin !== embedUrlOrigin) {
    return;
  }

  if (event.data.name === 'size' && iframeRef.value) {
    const { height } = event.data.payload;
    if (height) {
      iframeRef.value.style.height = `${height}px`;
    }
  }
}

onMounted(() => {
  generateEmbedUrl();
  window.addEventListener('message', handleMessage);
});

onUnmounted(() => {
  window.removeEventListener('message', handleMessage);
});
</script>

<style scoped>
.omni-dashboard {
  width: 100%;
  height: 100%;
  min-height: 600px;
}
</style>
